/**
 * Browser deep-link client (Phase 2)
 *
 * Calls the `mintBrowserDeepLink` Cloud Function and returns a signed
 * URL valid for 5 minutes that the AgentActivity page will validate.
 *
 * Falls back to an unsigned URL when the callable is unavailable
 * (e.g. unauthenticated user, dev environment without functions
 * deployed). The unsigned URL still loads the AgentActivity page —
 * the JWT just gates the privileged "live frames" view; without it,
 * the page degrades to a read-only summary.
 */

const isProduction =
  typeof window !== 'undefined' && window.location.hostname !== 'localhost';

function unsignedFallback(sessionId: string): string {
  const base = isProduction
    ? 'https://prest0.ai'
    : `${window.location.protocol}//${window.location.host}`;
  return `${base}/agent-activity?session=${encodeURIComponent(sessionId)}`;
}

export async function mintDeepLinkUrl(
  sessionId: string,
  uid?: string
): Promise<{ url: string; signed: boolean; expiresIn?: number }> {
  if (!sessionId) {
    return { url: '', signed: false };
  }
  try {
    // Lazy-load firebase to keep this module light.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getApp } = await import('firebase/app');
    const functions = getFunctions(getApp(), 'us-central1');
    const callable = httpsCallable<
      { sessionId: string; uid?: string; baseUrl?: string },
      { url: string; token: string; expiresIn: number }
    >(functions, 'mintBrowserDeepLink');
    const baseUrl = isProduction
      ? 'https://prest0.ai'
      : `${window.location.protocol}//${window.location.host}`;
    const result = await callable({ sessionId, uid, baseUrl });
    return {
      url: result.data.url,
      signed: true,
      expiresIn: result.data.expiresIn,
    };
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[browserDeepLinkClient] mintBrowserDeepLink failed; using unsigned fallback: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    }
    return { url: unsignedFallback(sessionId), signed: false };
  }
}
